// ------------------------------------------------------------------
// The Nordic Vision — non-translatable data (contact, routes, images).
// All copy lives in src/i18n/{en,sv,pl}.js
// ------------------------------------------------------------------

export const BRAND = {
  name: "The Nordic Vision",
  tagline: "Crafting Tomorrow",
};

export const CONTACT = {
  email: "info@thenordicvision.com",
  billingEmail: "faktura@thenordicvision.com",
  hrEmail: "hr@thenordicvision.com",
  phones: [
    { number: "+46 72 765 61 33", country: "SE", whatsapp: true, call: true },
  ],
  phone: "+46 72 765 61 33",
  whatsapp: "46727656133",
  countries: ["Sweden", "United Kingdom", "Portugal", "Poland", "Belgium", "Spain", "Germany", "Denmark", "Norway", "Finland", "Saudi Arabia"],
  countryCodes: ["se", "gb", "pt", "pl", "be", "es", "de", "dk", "no", "fi", "sa"],
  foundedYear: 2024,
  linkedin: "https://www.linkedin.com/company/the-nordic-vision",
  x: "https://x.com/thenordicvision",
  googleBusiness: "",
  addressLines: ["811 35 Sandviken, Sweden"],
  address: "811 35 Sandviken, Sweden",
  postalCode: "811 35",
  city: "Sandviken",
  orgNumber: "SE870616221401",
};

export const NAV_LINKS = [
  { id: "home", path: "/" },
  { id: "about", path: "/about" },
  { id: "services", children: [
    { id: "workforce", path: "/workforce" },
    { id: "hse", path: "/hse" },
    { id: "manpower", path: "/services" },
  ] },
  { id: "partners", path: "/about#partners" },
  { id: "contact", path: "/contact" },
];

// Shown as a client logo wall — project experience only, no endorsement implied.
// Logo files are self-hosted under /assets/logos (no external favicon API).
// `wide: false` marks square/emblem marks so the plate can size them differently.
// `scope`/`country` appear in the hover panel — project experience only.
export const CLIENTS = [
  { name: "Nesma & Partners", logo: "/assets/logos/nesma.svg", wide: false, scope: "HSE support and skilled trades on large construction packages", country: "Saudi Arabia" },
  { name: "Juffali & Brothers", logo: "/assets/logos/juffali.svg", wide: true, scope: "Industrial and infrastructure project support", country: "Saudi Arabia" },
  { name: "Hyundai Heavy Industries", logo: "/assets/logos/hyundai.png", wide: true, scope: "Heavy fabrication and shipyard trades", country: "South Korea" },
  { name: "Renfe Operadora", logo: "/assets/logos/renfe.png", wide: true, scope: "Rail infrastructure and maintenance works", country: "Spain" },
  { name: "PAC Group", logo: "/assets/logos/pacgroup.png", wide: true, scope: "Industrial project and safety services support", country: "Belgium" },
  { name: "Northvolt", logo: "/assets/logos/northvolt.png", wide: true, scope: "Battery gigafactory construction support", country: "Sweden" },
  { name: "Microsoft Data Centres", logo: "/assets/logos/microsoft.png", wide: true, scope: "Data centre construction and MEP crews", country: "Sweden" },
  { name: "Volvo Cars", logo: "/assets/logos/volvo.png", wide: false, scope: "Plant maintenance and production support", country: "Sweden" },
  { name: "Sarens", logo: "/assets/logos/sarens.png", wide: true, scope: "Heavy lift and crane operations support", country: "Belgium" },
  { name: "Vestas", logo: "/assets/logos/vestas.png", wide: true, scope: "Wind turbine installation and service", country: "Denmark" },
  { name: "Fairwind", logo: "/assets/logos/fairwind.svg", wide: true, scope: "Turbine installation and blade repair technicians", country: "Denmark" },
];

export const CLIENT_NAMES = CLIENTS.map((c) => c.name);

// Industry experience ribbon — environments our team and partner network have
// worked in. NOT clients, NOT endorsements, NOT current contracts.
export const INDUSTRY_EXPERIENCE = [
  { name: "Google Data Centre", logo: "/assets/logos/google.png", wide: true, scope: "Hyperscale data centre construction environments", sector: "Data centres" },
  { name: "Amazon", logo: "/assets/logos/amazon.png", wide: true, scope: "Logistics and fulfilment facility environments", sector: "Logistics" },
  { name: "PM Group", logo: "/assets/logos/pmgroup.svg", wide: false, scope: "Engineering and project delivery environments", sector: "Engineering" },
  { name: "NIRAS", logo: "", wide: true, scope: "Consulting engineering project environments", sector: "Consultancy" },
  { name: "Safety Symbiosis", logo: "/assets/logos/safetysymbiosis.png", wide: true, scope: "HSE and safety consultancy environments", sector: "HSE" },
  { name: "PepsiCo", logo: "/assets/logos/pepsico.png", wide: true, scope: "Food and beverage manufacturing environments", sector: "Manufacturing" },
  { name: "Walkers", logo: "/assets/logos/walkers.png", wide: false, scope: "Food production facility environments", sector: "Manufacturing" },
  { name: "Tesla", logo: "/assets/logos/tesla.png", wide: true, scope: "Automotive and battery manufacturing environments", sector: "Automotive" },
  { name: "BMW", logo: "/assets/logos/bmw.png", wide: false, scope: "Automotive production environments", sector: "Automotive" },
  { name: "Apollo Tyres", logo: "/assets/logos/apollotyres.png", wide: true, scope: "Tyre manufacturing plant environments", sector: "Manufacturing" },
  { name: "Pirelli", logo: "/assets/logos/pirelli.png", wide: true, scope: "Industrial manufacturing environments", sector: "Manufacturing" },
  { name: "Tesco", logo: "/assets/logos/tesco.png", wide: true, scope: "Distribution and retail facility environments", sector: "Retail" },
  { name: "Linde", logo: "/assets/logos/linde.png", wide: true, scope: "Industrial gas and process plant environments", sector: "Industrial gases" },
  { name: "ORLEN", logo: "/assets/logos/orlen.png", wide: true, scope: "Refinery and petrochemical environments", sector: "Energy" },
  { name: "Mars", logo: "/assets/logos/mars.png", wide: true, scope: "Food manufacturing environments", sector: "Manufacturing" },
  { name: "KUKA Robotics", logo: "/assets/logos/kuka.png", wide: true, scope: "Automation and robotics integration environments", sector: "Automation" },
];

export const IMAGES = {
  hero: "/assets/hero.webp",
  construction: "/assets/construction-1.webp",
  constructionAlt: "/assets/construction-2.webp",
  industrial: "/assets/industry-1.webp",
  industrialAlt: "/assets/industry-2.webp",
  team: "/assets/team.webp",
  planning: "/assets/planning.webp",
};

// Responsive srcSet: 800px WebP for phones, full 1400px for larger screens.
export const srcSetOf = (key) => `${IMAGES[key].replace(".webp", "-800.webp")} 800w, ${IMAGES[key]} 1400w`;
export const IMG_SIZES = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px";

export const MARKETS = [
  { code: "se", mapName: "Sweden",   coords: [15.6, 62.0], whatsapp: "46727656133",  hq: true },
  { code: "gb", mapName: "United Kingdom", coords: [-2.0, 53.5], whatsapp: "447774924524", hq: false },
  { code: "pt", mapName: "Portugal", coords: [-8.2, 39.5], whatsapp: "46727656133", hq: false },
  { code: "pl", mapName: "Poland",   coords: [19.1, 52.1], whatsapp: "48660459630",  hq: false },
  { code: "be", mapName: "Belgium",  coords: [4.5, 50.6],  whatsapp: "46727656133",  hq: false },
  { code: "es", mapName: "Spain",    coords: [-3.7, 40.2], whatsapp: "46727656133", hq: false },
  { code: "de", mapName: "Germany",  coords: [10.4, 51.1], whatsapp: "46727656133",  hq: false },
  { code: "dk", mapName: "Denmark",  coords: [9.5, 56.0],  whatsapp: "46727656133",  hq: false },
  { code: "no", mapName: "Norway",   coords: [8.9, 61.2],  whatsapp: "46727656133",  hq: false },
  { code: "fi", mapName: "Finland",  coords: [25.7, 62.5], whatsapp: "46727656133",  hq: false },
  { code: "sa", mapName: "Saudi Arabia", coords: [45.1, 23.9], whatsapp: "46727656133", hq: false },
];

// All candidate/market enquiries route to the single HR mailbox.
export const COUNTRY_HR_EMAILS = {};

export const OPERATING_MAP_NAMES = MARKETS.map((m) => m.mapName);

// lucide-react icon names for the 12 HSE services (order matches i18n hse.services)
export const HSE_SERVICE_ICONS = ["Zap", "Box", "MoveVertical", "ClipboardCheck", "Flame", "Lock", "FlaskConical", "Construction", "Sparkles", "Truck", "Siren", "HardHat"];

export const INDUSTRY_IDS = ["construction", "industry"];
export const INDUSTRY_IMAGES = ["construction", "industrial"];
export const SERVICE_ICONS = ["Search", "UserCheck", "ClipboardList", "LineChart", "RefreshCw", "Network", "Factory", "ShieldCheck"];
export const BENEFIT_ICONS = ["UserCheck", "CalendarRange", "MessagesSquare"];

// Values stored in the database (English, stable across languages)
export const SECTOR_VALUES = ["Construction", "Industry & Metal", "Other"];
export const AVAILABILITY_VALUES = ["Immediately", "Within 2 weeks", "Within 1 month", "Flexible / to be discussed"];

export const ORG = {
  head: { title: "Managing Director / CEO", text: "Overall leadership & strategic direction" },
  departments: [
    { id: "operations", icon: "Cog", title: "Operations & Projects", tagline: "Delivering projects with excellence", roles: ["Operations Manager", "Project Managers", "Site Supervisors", "Planning & Scheduling", "Logistics & Procurement"] },
    { id: "manpower", icon: "Users", title: "Manpower Supply", tagline: "Providing the right people", roles: ["Manpower Coordinator", "Workforce Planning", "Deployment & Mobilization", "Site Support", "Demobilization & Repatriation"] },
    { id: "hse", icon: "ShieldCheck", title: "HSE & Quality Management", tagline: "Safety · Quality · Compliance", roles: ["HSE Manager / Lead", "HSE Advisors / Specialists", "Safety Officers", "Quality / Compliance Officers", "Training & Competency"] },
    { id: "recruitment", icon: "Search", title: "Recruitment & Talent Acquisition", tagline: "Finding. Screening. Placing.", roles: ["Recruitment Manager", "Sourcing & Screening", "Interviews & Assessment", "Documentation & Verification", "Database & Talent Management"] },
    { id: "finance", icon: "TrendingUp", title: "Finance & Administration", tagline: "Financial strength. Operational support.", roles: ["Finance Manager", "Accounting", "Payroll", "Administration", "Contracts & Documentation"] },
    { id: "business", icon: "Handshake", title: "Business Development", tagline: "Growing relationships. Creating opportunities.", roles: ["Business Development Manager", "Client Relations", "Tendering & Proposals", "Marketing & Branding", "Strategic Partnerships"] },
  ],
};
